import * as THREE from 'three';
import { prefersTouchUi } from '../input/touch';
import { registerKtx2Renderer } from './ktx2';

/**
 * Renderer bootstrap (owner: world-artist from here on). Creates the WebGLRenderer with the
 * project-wide colour pipeline: sRGB output, ACES filmic tonemapping, PCF-filtered shadows.
 *
 * Phones (docs/PERF.md, "Phone performance"): the canvas itself is not multisampled there. MEDIUM and
 * HIGH anti-alias inside the post stack's own MSAA target, so a multisampled default framebuffer would
 * only add memory (4 samples × colour + depth at the drawing-buffer size) and a resolve per frame.
 *
 * WebGL context loss (iOS drops the context of a backgrounded home-screen app, any browser can after a
 * GPU reset): the 'webglcontextlost' default is prevented so the browser may restore it, the render
 * system skips drawing while it is gone (`contextWatch(renderer).lost`), a small "Resuming…" card covers
 * the frozen picture, and on 'webglcontextrestored' three.js re-uploads geometry, textures and render
 * targets on the next draw; listeners (`onRestored`) rebuild what was *rendered* into a texture (the PMREM
 * environments) and the post stack. If no restore comes within a few seconds the card offers a reload.
 */
export interface ContextWatch {
  /** True between 'webglcontextlost' and 'webglcontextrestored'. */
  readonly lost: boolean;
  /** Times the context came back. */
  readonly restores: number;
  /** Times it was lost. */
  readonly losses: number;
  /** Called after a restore (three.js has re-initialised its GL state). Returns an unsubscribe. */
  onRestored(fn: () => void): () => void;
}

const watches = new WeakMap<THREE.WebGLRenderer, ContextWatch>();

/** The context-loss state of a renderer made by createRenderer (a never-lost stub otherwise). */
export function contextWatch(renderer: THREE.WebGLRenderer): ContextWatch {
  return watches.get(renderer) ?? { lost: false, restores: 0, losses: 0, onRestored: () => () => {} };
}

/** Seconds without a restore before the card offers a reload. */
const RELOAD_AFTER = 6;

export interface RendererHandle {
  renderer: THREE.WebGLRenderer;
  canvas: HTMLCanvasElement;
  /** Re-reads container size; called automatically on window resize. */
  resize(): void;
  dispose(): void;
}

export function createRenderer(container: HTMLElement, camera: THREE.PerspectiveCamera): RendererHandle {
  const touch = prefersTouchUi();
  const renderer = new THREE.WebGLRenderer({ antialias: !touch, powerPreference: 'high-performance', stencil: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.2;
  renderer.shadowMap.enabled = true;
  // three r186 removed PCFSoftShadowMap (it warned and fell back to PCFShadowMap on every boot);
  // PCFShadowMap is the supported soft-filtered equivalent (light.shadow.radius tunes it).
  renderer.shadowMap.type = THREE.PCFShadowMap;
  // The post pipeline renders several passes per frame; it resets the counters once per frame.
  renderer.info.autoReset = false;
  const canvas = renderer.domElement;
  canvas.id = 'game-canvas';
  canvas.tabIndex = 0;
  container.appendChild(canvas);
  // The model loaders' KTX2 transcoder picks the GPU's compressed formats from this renderer (render/ktx2.ts).
  registerKtx2Renderer(renderer);

  const resize = (): void => {
    const w = container.clientWidth || window.innerWidth;
    const h = container.clientHeight || window.innerHeight;
    const cur = renderer.getSize(new THREE.Vector2());
    if (cur.x !== w || cur.y !== h) renderer.setSize(w, h, false);
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    camera.aspect = w / Math.max(h, 1);
    camera.updateProjectionMatrix();
  };
  // Phones change size without a reliable window `resize`: rotating out of the portrait card, entering
  // fullscreen / standalone, the address bar sliding away. A missed one leaves the old drawing buffer
  // stretched over the new box (a squashed picture). Follow the container itself, plus the events
  // that fire before layout settles, re-checked on the next frames.
  let settle = 0;
  const resizeSoon = (): void => {
    resize();
    cancelAnimationFrame(settle);
    let frames = 0;
    const again = (): void => {
      resize();
      if (++frames < 10) settle = requestAnimationFrame(again);
    };
    settle = requestAnimationFrame(again);
  };
  resize();
  window.addEventListener('resize', resizeSoon);
  window.addEventListener('orientationchange', resizeSoon);
  document.addEventListener('fullscreenchange', resizeSoon);
  window.visualViewport?.addEventListener('resize', resizeSoon);
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => resize());
  observer?.observe(container);

  // ---- context loss
  const restoredFns = new Set<() => void>();
  const watch = { lost: false, restores: 0, losses: 0, onRestored: (fn: () => void) => (restoredFns.add(fn), () => void restoredFns.delete(fn)) };
  watches.set(renderer, watch);
  let card: HTMLDivElement | null = null;
  let reloadTimer = 0;
  const showCard = (): void => {
    if (!card) {
      card = document.createElement('div');
      card.id = 'bh-context-lost';
      card.setAttribute('role', 'status');
      card.style.cssText =
        'position:fixed;inset:0;z-index:1900;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:0.9rem;' +
        'background:rgba(18,18,20,0.82);color:#e9e2cf;font-family:Georgia,serif;letter-spacing:0.3em;text-transform:uppercase;font-size:0.85rem;pointer-events:auto';
      card.innerHTML = '<div class="msg">Resuming…</div>';
      document.body.appendChild(card);
    }
    card.style.display = 'flex';
    window.clearTimeout(reloadTimer);
    reloadTimer = window.setTimeout(() => {
      if (!watch.lost || !card || card.querySelector('button')) return;
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = 'Reload';
      b.style.cssText =
        'font:inherit;letter-spacing:0.25em;text-transform:uppercase;color:#121214;background:#d9b45a;border:0;border-radius:3px;min-width:44px;min-height:44px;padding:0 1.4rem;cursor:pointer';
      b.addEventListener('click', () => location.reload());
      card.appendChild(b);
    }, RELOAD_AFTER * 1000);
  };
  const hideCard = (): void => {
    window.clearTimeout(reloadTimer);
    if (!card) return;
    card.style.display = 'none';
    card.querySelector('button')?.remove();
  };
  const onLost = (e: Event): void => {
    // Without this the browser never fires 'webglcontextrestored' (three.js also prevents it).
    e.preventDefault();
    watch.lost = true;
    watch.losses++;
    cancelAnimationFrame(settle);
    showCard();
  };
  const onRestored = (): void => {
    watch.lost = false;
    watch.restores++;
    // three.js re-initialised its GL state in its own listener (registered first, in its constructor).
    resizeSoon();
    renderer.shadowMap.needsUpdate = true;
    for (const fn of [...restoredFns]) fn();
    hideCard();
  };
  canvas.addEventListener('webglcontextlost', onLost);
  canvas.addEventListener('webglcontextrestored', onRestored);

  return {
    renderer,
    canvas,
    resize,
    dispose() {
      window.removeEventListener('resize', resizeSoon);
      window.removeEventListener('orientationchange', resizeSoon);
      document.removeEventListener('fullscreenchange', resizeSoon);
      window.visualViewport?.removeEventListener('resize', resizeSoon);
      observer?.disconnect();
      cancelAnimationFrame(settle);
      canvas.removeEventListener('webglcontextlost', onLost);
      canvas.removeEventListener('webglcontextrestored', onRestored);
      window.clearTimeout(reloadTimer);
      card?.remove();
      restoredFns.clear();
      renderer.dispose();
      canvas.remove();
    },
  };
}
